import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import axios from 'axios';
import { getErrorMessage, createApiClient } from '../utils/api';
import './UserConfiguration.css';

interface User {
  username: string;
  selected: boolean;
  amount: string;
  manager_username?: string;
}

interface UserWithManager {
  username: string;
  manager_username: string;
}

const UserConfiguration: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const navigate = useNavigate();

  const apiClient = createApiClient(10000); // 10 second timeout for user loading
  const allocationClient = createApiClient(30000); // 30 second timeout for allocation

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/users');
      
      // Use users_with_managers if available, otherwise fall back to users array
      let usersWithManagers: UserWithManager[] = [];
      if (response.data.users_with_managers) {
        usersWithManagers = response.data.users_with_managers;
      } else {
        // Fallback: create structure from simple users array
        usersWithManagers = response.data.users.map((username: string) => ({
          username,
          manager_username: 'Unassigned'
        }));
      }
      
      const userList = usersWithManagers.map((u: UserWithManager) => ({
        username: u.username,
        manager_username: u.manager_username,
        selected: false,
        amount: ''
      }));
      
      // Load saved configuration and merge with user list
      let savedConfig = localStorage.getItem('port-reallocation-config');
      if (!savedConfig) {
        savedConfig = Cookies.get('port-reallocation-config') || null;
      }
      
      console.log('Loading saved config:', savedConfig);
      
      if (savedConfig) {
        try {
          const config = JSON.parse(savedConfig);
          console.log('Parsed config:', config);
          const mergedUsers = userList.map((user: User) => {
            const savedUser = config.find((s: any) => s.username === user.username);
            if (savedUser) {
              console.log(`Found saved user ${savedUser.username}:`, savedUser);
              console.log(`Restoring ${savedUser.username}: selected=${savedUser.selected}, amount=${savedUser.amount}`);
              return { 
                username: user.username,
                manager_username: user.manager_username,
                selected: true,  // Force selected to true if we found a saved user
                amount: savedUser.amount || '' 
              };
            }
            return user;
          });
          console.log('Merged users:', mergedUsers.filter((u: User) => u.selected));
          setUsers(mergedUsers);
        } catch (err) {
          console.error('Error loading saved configuration:', err);
          setUsers(userList);
        }
      } else {
        console.log('No saved configuration found');
        setUsers(userList);
      }
    } catch (err: any) {
      setError(getErrorMessage(err));
      console.error('Error loading users:', err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfiguration = () => {
    const config = users.filter(user => user.selected);
    const configStr = JSON.stringify(config);
    // Save to both localStorage and cookies for redundancy
    localStorage.setItem('port-reallocation-config', configStr);
    Cookies.set('port-reallocation-config', configStr, { expires: 30 });
  };

  const handleUserToggle = (username: string) => {
    setUsers(prevUsers => {
      const updated = prevUsers.map(user => 
        user.username === username 
          ? { ...user, selected: !user.selected, amount: user.selected ? '' : user.amount }
          : user
      );
      // Auto-save on change
      setTimeout(() => {
        const config = updated.filter(u => u.selected);
        const configStr = JSON.stringify(config);
        localStorage.setItem('port-reallocation-config', configStr);
        Cookies.set('port-reallocation-config', configStr, { expires: 30 });
        console.log('Configuration saved:', config);
      }, 100);
      return updated;
    });
  };

  const handleAmountChange = (username: string, amount: string) => {
    setUsers(prevUsers => {
      const updated = prevUsers.map(user => 
        user.username === username 
          ? { ...user, amount }
          : user
      );
      // Auto-save on change
      setTimeout(() => {
        const config = updated.filter(u => u.selected);
        const configStr = JSON.stringify(config);
        localStorage.setItem('port-reallocation-config', configStr);
        Cookies.set('port-reallocation-config', configStr, { expires: 30 });
        console.log('Configuration saved:', config);
      }, 100);
      return updated;
    });
  };

  const handleRunAllocation = async () => {
    const selectedUsers = users.filter(user => user.selected && user.amount);
    
    if (selectedUsers.length === 0) {
      alert('Please select at least one user and enter amounts');
      return;
    }

    // Validate amounts
    for (const user of selectedUsers) {
      const amount = parseFloat(user.amount);
      if (isNaN(amount) || amount <= 0) {
        alert(`Please enter a valid amount for ${user.username}`);
        return;
      }
    }

    try {
      setRunning(true);
      saveConfiguration();
      
      const response = await allocationClient.post('/run-allocation', {
        users: selectedUsers.map(user => ({
          username: user.username,
          amount: parseFloat(user.amount)
        }))
      });

      if (response.data.success) {
        // Store results in session storage for results page
        sessionStorage.setItem('allocation-results', JSON.stringify(response.data.results));
        // Also store config in localStorage for download
        localStorage.setItem('port-reallocation-config', JSON.stringify(selectedUsers.map(user => ({
          username: user.username,
          amount: parseFloat(user.amount)
        }))));
        navigate('/port-reallocation/results');
      } else {
        alert('Failed to run allocation: ' + response.data.error);
      }
    } catch (err: any) {
      alert('Error running allocation: ' + getErrorMessage(err));
      console.error('Error running allocation:', err);
    } finally {
      setRunning(false);
    }
  };

  const selectedCount = users.filter(user => user.selected).length;
  const totalAmount = users
    .filter(user => user.selected && user.amount)
    .reduce((sum, user) => sum + (parseFloat(user.amount) || 0), 0);

  if (loading) {
    return (
      <div className="user-config">
        <div className="loading">Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-config">
        <div className="error">{error}</div>
        <button onClick={loadUsers} className="retry-button">Retry</button>
      </div>
    );
  }

  return (
    <div className="user-config">
      <div className="config-header">
        <h2>Account Manager Configuration</h2>
        <p>Select account managers and specify the amount to allocate from each manager's portfolio.</p>
      </div>

      <div className="config-summary">
        <div className="summary-item">
          <span className="summary-label">Selected Users:</span>
          <span className="summary-value">{selectedCount}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Total Amount:</span>
          <span className="summary-value">${totalAmount.toLocaleString()}</span>
        </div>
      </div>

      <div className="users-list">
        {(() => {
          // Group users by manager
          const groupedByManager = users.reduce((acc, user) => {
            const manager = user.manager_username || 'Unassigned';
            if (!acc[manager]) {
              acc[manager] = [];
            }
            acc[manager].push(user);
            return acc;
          }, {} as Record<string, User[]>);

          // Sort managers alphabetically
          const sortedManagers = Object.keys(groupedByManager).sort();

          return sortedManagers.map(manager => (
            <div key={manager} className="manager-group">
              <div className="manager-header">
                <h3>{manager}</h3>
                <span className="manager-count">
                  {groupedByManager[manager].length} account manager{groupedByManager[manager].length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="manager-users">
                {groupedByManager[manager].map(user => (
                  <div key={user.username} className={`user-item ${user.selected ? 'selected' : ''}`}>
                    <div className="user-checkbox">
                      <input
                        type="checkbox"
                        id={`user-${user.username}`}
                        checked={user.selected}
                        onChange={() => handleUserToggle(user.username)}
                      />
                      <label htmlFor={`user-${user.username}`} className="user-label">
                        {user.username}
                      </label>
                    </div>
                    {user.selected && (
                      <div className="user-amount">
                        <label htmlFor={`amount-${user.username}`} className="amount-label">
                          Amount ($):
                        </label>
                        <input
                          type="number"
                          id={`amount-${user.username}`}
                          value={user.amount}
                          onChange={(e) => handleAmountChange(user.username, e.target.value)}
                          placeholder="Enter amount"
                          min="0"
                          step="0.01"
                          className="amount-input"
                        />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ));
        })()}
      </div>

      <div className="config-actions">
        <button 
          onClick={handleRunAllocation}
          disabled={running || selectedCount === 0}
          className="run-button"
        >
          {running ? 'Running Allocation...' : 'Run Port Reallocation'}
        </button>
      </div>
    </div>
  );
};

export default UserConfiguration;
